import type { FundusScreeningThirdParty } from '@common/fundusScreening/thirdParties';
import type { ExcelParserValidationError } from '@common/interfaces/excelParser';
import type { ZipServiceFile } from '@common/interfaces/zipService';

export type FundusScreeningManualImportApiResponse = {
    excelParser: {
        errors:
            | ExcelParserValidationError<HealthConnectedManualImportPatient>[]
            | ExcelParserValidationError<VipLiveManualImportPatient>[];
    };
    success: boolean;
    zipFile: ZipServiceFile | null;
};

export type FundusScreeningGetManualImportLogsApiResponse = {
    logs: FundusScreeningManualImportLogFrontend[];
    totalItems: number;
};

export type FundusScreeningManualImportLogsSortByProperties = Pick<
    FundusScreeningManualImportLogFrontend,
    'importDate' | 'importedFileName' | 'invitationType'
> & {
    'group.name': string;
    'modifierPractitioner.nameFamily': string;
    'thirdParty.name': string;
};

export enum FundusScreeningManualImportLogInvitationType {
    initialInvitation = 'initialInvitation',
    repeatedInvitation = 'repeatedInvitation',
}

export type FundusScreeningManualImportLogFrontend = {
    careGroupName: string;
    downloadUrl: string;
    exportedFileName: string;
    id: string;
    importDate: Date;
    importedBy: string;
    importedFileName: string;
    invitationType: FundusScreeningManualImportLogInvitationType;
    thirdPartyName: FundusScreeningThirdParty;
};

export enum FundusPatientFollowUpAdviceInterval {
    noFundusControlNeededAnymore = 'geen funduscontrole meer noodzakelijk',
    oneDay = 'binnen 1 dag bij oogarts',
    oneMonth = 'binnen 1 maand bij oogarts',
    oneWeek = 'binnen 1 week bij oogarts',
    oneYear = '1 jaar (reguliere controle)',
    sixMonths = 'binnen 6 maanden bij oogarts',
    threeMonths = 'binnen 3 maanden bij oogarts',
    threeYears = '3 jaar (reguliere controle)',
    twoYears = '2 jaar (reguliere controle)',
}

export type FundusScreeningManualImportPatient = HealthConnectedManualImportPatient | VipLiveManualImportPatient;

export type HealthConnectedManualImportPatient = {
    Achternaam: string; //nameFamily
    BSN: string; //identifierSocialSecurity
    'Datum-beoordeling': Date | null; // lastEvaluationDate
    Emailadres: string | null; //emailAddress
    'Followup-nhgresult': number | null;
    Geboortedatum: Date; //birthDate
    Geslachtsaanduiding: string; //gender
    HuisartsAGB: string | null; //practitioner AGB
    HuisartsPraktijkAGB: string; //organization AGB
    Huisnummer: string | null; //houseNumber
    Huisnummertoevoeging: string | null; //houseNumberSuffix
    lastExtractionFromHisByHealthConnected: Date | string | null;
    Partner_achternaam: string | null; //nameFamily2
    Partner_tussenvoegsel: string | null; //nameInfix2
    postcode: string | null; //zipCode
    Straatnaam: string | null; //street
    Telefoonnummer: string | null; //phoneNumber
    Tussenvoegsels: string | null; //nameGiven
    Voorletter: string | null; //nameInitials
    Voornamen: string | null; // firstName
    woonplaatsnaam: string | null; //city
};

export type VipLiveManualImportPatient = {
    'Aanvragende praktijk': string; //GP organizationName + (AGB code)
    'Ontvangende praktijk': string; //CaregroupName + (reference)
    'Patiënt adres': string; //address → street + houseNumber
    'Patiënt BSN': string; //identifierSocialSecurity
    'Patiënt geboortedatum': string; //birthDate
    'Patiënt geslacht': string; //gender
    'Patiënt naam': string; //name
    'Patiënt postcode': string; //zipCode
    'Patiënt woonplaats': string; //city
    Verwijsdatum: string; //Referral date
};
